import React from 'react';

// AB#210766 - password strength meter shown under password fields on
// registration / set-password / reset-password / profile-change screens.
// Dependency-free entropy-style heuristic (length + character-class variety,
// with penalties for repeats / sequences / common passwords).

interface PasswordStrengthMeterProps {
  password: string;
}

const LABELS = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'];
const COLOURS = ['#d64545', '#e2711d', '#e2b93b', '#5a9e3b', '#2f8f4e'];

const COMMON = [
  'password',
  '123456',
  '12345678',
  'qwerty',
  'letmein',
  'welcome',
  'admin',
  'iloveyou',
  'monkey',
  'abc123',
];

function classesUsed(pw: string): number {
  return [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9\s]/].filter((r) => r.test(pw))
    .length;
}

function missingHint(pw: string): string {
  if (pw.length < 10) return 'Use at least 10 characters';
  if (!/[a-z]/.test(pw)) return 'Add a lowercase letter';
  if (!/[A-Z]/.test(pw)) return 'Add an uppercase letter';
  if (!/\d/.test(pw)) return 'Add a number';
  if (!/[^A-Za-z0-9\s]/.test(pw)) return 'Add a special character';
  if (pw.length < 14) return 'Longer passwords are stronger';
  return '';
}

function scorePassword(pw: string): number {
  if (!pw) return 0;

  const lower = pw.toLowerCase();
  if (COMMON.some((c) => lower.includes(c))) return 0;

  let score = 0;
  const len = pw.length;
  const classes = classesUsed(pw);

  if (len >= 10) score += 1;
  if (len >= 14) score += 1;
  if (len >= 18) score += 1;
  if (classes >= 3) score += 1;
  if (classes === 4 && len >= 12) score += 1;

  // Penalties for low-entropy shapes.
  if (/(.)\1{2,}/.test(pw)) score -= 1; // 3+ repeated characters
  if (/^[A-Za-z]+$/.test(pw) || /^\d+$/.test(pw)) score = Math.min(score, 1);

  return Math.max(0, Math.min(4, score));
}

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
}) => {
  if (!password) {
    return null;
  }

  const score = scorePassword(password);
  const label = LABELS[score];
  const colour = COLOURS[score];
  const hint = missingHint(password);

  return (
    <div
      className="PasswordStrengthMeter"
      aria-live="polite"
      // Pull up towards the password field above (offsets the field's 20px bottom
      // margin, which sits below any validation message so this won't overlap it),
      // and leave more space below so the meter reads as attached to that field.
      style={{ marginTop: -12, marginBottom: 18 }}
    >
      <div style={{ display: 'flex', gap: 4, height: 6, marginBottom: 4 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: i <= score ? colour : '#e0e0e6',
              transition: 'background-color 150ms ease',
            }}
          />
        ))}
      </div>
      <div style={{ fontSize: 12, color: colour, fontWeight: 600 }}>
        Password strength: {label}
      </div>
      {hint && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
          {hint}
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;
